import {Feature} from "@luciad/ria/model/feature/Feature.js";
import * as React from "react";
import {Alert, Button, Col, ControlLabel, Form, FormControl, FormGroup, Modal} from "react-bootstrap/lib";
import {FormattedMessage, InjectedIntlProps, injectIntl} from "react-intl";
import {ErrorDisplay} from "../common/ui/errordisplay/ErrorDisplay";
import {RESTRICT_NAVIGATION_BOUNDS} from "../common/ui/riamap/Previewer";
import {RIAMap} from "../common/ui/riamap/RIAMap";
import {Api} from "../api/InternalApi";
import {Spinner} from "../common/ui/spinner/Spinner";
import {mapErrorToMessage} from "../common/util/ErrorMapper";
import {WithApi, WithApiProperties} from "../common/util/WithApi";
import {AOINotificationArea, AOIMonitor} from "./model";
import {getFeatureFromGeometryString, getGeometryStringFromFeature} from "../security/model";

const LABEL_COL = 2;
const CONTROL_COL = 10;

export interface AOIDialogOwnProps {
  onSuccess?: () => void;
  onHide?: () => void;
  notification: AOIMonitor;
  notificationAreaIndex: number;
  api: Api;
}

type AOIDialogProps = AOIDialogOwnProps & InjectedIntlProps & WithApiProperties;

interface AOIDialogState {
  isExecuting: boolean;
  isSuccess: boolean;
  error: Error;
  name: string;
  mapDrawModeActive: boolean;
  mapEditModeActive: boolean;
  spatialAreaFeature: Feature;
  spatialAreaFeatureBefore: Feature;
  notificationArea?: AOINotificationArea;
}

class AOIDialogComponent extends React.Component<AOIDialogProps, AOIDialogState> {

  constructor(props: AOIDialogProps) {
    super(props);
    const notificationAreas = props.notification.triggers;
    const notificationArea = props.notificationAreaIndex == null ? null
                                                                 : notificationAreas[props.notificationAreaIndex];
    const spatialAreaFeature = getFeatureFromGeometryString(notificationArea?.geomWkt);

    this.state = {
      isExecuting: false,
      error: null,
      isSuccess: false,
      notificationArea,
      name: notificationArea?.name || "",
      mapDrawModeActive: false,
      mapEditModeActive: false,
      spatialAreaFeature,
      spatialAreaFeatureBefore: null,
    };
  }

  render() {
    return (
        <div>
          {this.renderModal()}
        </div>
    );
  }

  private closeDialog = () => {
    this.props.onHide();
  }

  private renderTitle() {
    const {notificationAreaIndex} = this.props;
    const {isExecuting, isSuccess} = this.state;

    if (isExecuting || isSuccess) {
      return null;
    }

    if (notificationAreaIndex !== null) {
      return <Modal.Title><FormattedMessage id="studio.notifications.aoi-dialog.edit-aoi"
                                            defaultMessage="Edit AOI"/></Modal.Title>;
    }

    return <Modal.Title><FormattedMessage id="studio.notifications.create-aoi"
                                          defaultMessage="Create AOI"/></Modal.Title>;
  }

  private renderButtons() {
    const {notificationAreaIndex} = this.props;
    const {isExecuting, isSuccess} = this.state;

    if (isExecuting || isSuccess) {
      return null;
    }

    return (
        <div>
          <Button onClick={this.submit}
                  disabled={this.state.name.length === 0 || !this.state.spatialAreaFeature}>
            {notificationAreaIndex == null &&
             <FormattedMessage id="studio.form.create" defaultMessage="Create"/>}
            {notificationAreaIndex != null && <FormattedMessage id="studio.form.save" defaultMessage="Save"/>}
          </Button>

          <Button onClick={this.closeDialog}><FormattedMessage id="studio.form.cancel"
                                                               defaultMessage="Cancel"/></Button>
        </div>
    );
  }

  private renderExecuting() {
    const {isExecuting} = this.state;
    if (isExecuting) {
      return (
          <div style={{textAlign: "center"}}>
            <h2><FormattedMessage id="studio.notifications.aoi-dialog.saving" defaultMessage="Saving AOI..."/>
            </h2>
            <Spinner bigStyle/>
          </div>
      );
    }
    return null;
  }

  private renderError() {
    const {error} = this.state;

    if (error) {
      const errorMessage = mapErrorToMessage(error);
      if (errorMessage) {
        return <Alert bsStyle="danger">{errorMessage}</Alert>;
      }
      return <ErrorDisplay error={error}/>;
    }
    return null;
  }

  private renderMap() {
    const {api} = this.props;
    const background = api.getPreviewBackground();

    return <RIAMap style={{width: "100%", height: "400px"}}
                   api={this.props.api}
                   background={background}
                   dataToVisualize={[]}
                   shouldFit={true}
                   shouldAnimateFit={false}
                   restrictNavigationBounds={background.bounds ? background.bounds : RESTRICT_NAVIGATION_BOUNDS}
                   shouldHideLayerTree={true}
                   shouldAddGridLayer={false}
                   showPreview={true}
                   drawModeActive={this.state.mapDrawModeActive}
                   editModeActive={this.state.mapEditModeActive}
                   onDrawCancel={this.onDrawEditCancel}
                   onEditCancel={this.onDrawEditCancel}
                   editFeature={this.state.spatialAreaFeature}
                   alwaysShowEditFeature={true}
                   onFeatureDrawn={this.onFeatureDrawn}
                   button={this.riaMapButton()}
    />;
  }

  private renderBody() {
    const {isExecuting} = this.state;

    if (isExecuting) {
      return this.renderExecuting();
    }

    return (
        <div>
          {this.renderError()}
          <Form horizontal>
            <FormGroup controlId="name">
              <Col componentClass={ControlLabel} sm={LABEL_COL}>
                <FormattedMessage id="studio.notifications.aoi-dialog.name"
                                  defaultMessage="Name"/>
              </Col>
              <Col sm={CONTROL_COL}>
                <FormControl name="name" value={this.state.name} onChange={this.onChange}/>
              </Col>
            </FormGroup>
            <FormGroup controlId="riamap">
              <Col componentClass={ControlLabel} sm={LABEL_COL}>
              </Col>
              <Col sm={CONTROL_COL}>
                {this.state.mapEditModeActive &&
                 this.renderMap()
                }
                {!this.state.mapEditModeActive &&
                 this.renderMap()
                }
              </Col>
            </FormGroup>
          </Form>
        </div>
    );
  }

  private onChange = (event: any) => {
    const {name} = event.target;
    let {value} = event.target;
    value = event.target.type === 'checkbox' ? event.target.checked : value;
    this.setState({[name]: value} as any);
    this.render()
  };

  private riaMapButton = () => {
    const {spatialAreaFeature} = this.state;

    return <div className="riamapButtons">
      <Button onClick={this.onDraw}>
        <FormattedMessage
            id="studio.ria-map.draw"
            defaultMessage="Draw"/>
      </Button>

      <Button onClick={this.onEdit} disabled={!spatialAreaFeature}>
        <FormattedMessage
            id="studio.ria-map.edit"
            defaultMessage="Edit"/>
      </Button>
    </div>;
  };

  private onDraw = () => {
    this.setState(prevState => ({
      mapDrawModeActive: true,
      spatialAreaFeatureBefore: prevState.spatialAreaFeature,
      spatialAreaFeature: null,
    }));
  };

  private onEdit = () => {
    this.setState(prevState => ({
      mapEditModeActive: true,
      spatialAreaFeatureBefore: prevState.spatialAreaFeature,
    }));
  };

  private onDrawEditCancel = () => {
    this.setState(prevState => ({
      mapDrawModeActive: false,
      mapEditModeActive: false,
      spatialAreaFeature: prevState.spatialAreaFeatureBefore,
    }));
  };

  private onFeatureDrawn = (spatialAreaFeature: Feature) => {
    this.setState({
      ...this.state,
      spatialAreaFeature,
      mapDrawModeActive: false,
      mapEditModeActive: false,
    });
  };

  private renderModal() {
    const {isExecuting} = this.state;
    return (
        <Modal show={true} onHide={this.closeDialog} backdrop="static">
          <Modal.Header closeButton={!isExecuting}>
            {this.renderTitle()}
          </Modal.Header>
          <Modal.Body>
            {this.renderBody()}
          </Modal.Body>
          <Modal.Footer>
            {this.renderButtons()}
          </Modal.Footer>
        </Modal>
    );
  }

  private submit = () => {
    const {notificationArea} = this.state;

    Promise.resolve()
        .then(() => {
          const {api, notification} = this.props;
          const {name, spatialAreaFeature} = this.state;
          const geomWkt = getGeometryStringFromFeature(spatialAreaFeature);
          this.setState({...this.state, isExecuting: true});
          return api.updateAOINotification(notification, {
            id: notificationArea?.id,
            name,
            srs: "4326",
            geomWkt
          })
              .catch((error) => {
                this.setState({...this.state, isExecuting: false, error});

                throw error;
              })
              .then(() => {
                this.props.onSuccess();
              });
        });
  }
}

export const AOIDialog = WithApi(injectIntl(AOIDialogComponent));